import { createHash, randomBytes } from 'crypto';

/** Number of one-time backup codes issued per 2FA enable/regenerate. */
export const TWO_FACTOR_BACKUP_CODE_COUNT = 8;

/** Stored backup code length excluding optional display dash (8 hex chars). */
export const TWO_FACTOR_BACKUP_CODE_LENGTH = 8;

export function normalizeTwoFactorBackupCodeInput(input: string): string {
  return input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

export function formatTwoFactorBackupCode(raw: string): string {
  const normalized = normalizeTwoFactorBackupCodeInput(raw);
  if (normalized.length !== TWO_FACTOR_BACKUP_CODE_LENGTH) return normalized;
  return `${normalized.slice(0, 4)}-${normalized.slice(4)}`;
}

export function isValidTwoFactorBackupCodeInput(input: string): boolean {
  return normalizeTwoFactorBackupCodeInput(input).length === TWO_FACTOR_BACKUP_CODE_LENGTH;
}

export function isLikelyTotpCode(input: string): boolean {
  const digits = input.replace(/\D/g, '');
  return digits.length === 6 && digits === input.replace(/\s/g, '');
}

export function hashTwoFactorBackupCode(code: string): string {
  const normalized = normalizeTwoFactorBackupCodeInput(code);
  return createHash('sha256').update(normalized, 'utf8').digest('hex');
}

export function generateTwoFactorBackupCodes(count = TWO_FACTOR_BACKUP_CODE_COUNT): string[] {
  const codes = new Set<string>();
  while (codes.size < count) {
    const raw = randomBytes(4).toString('hex').toUpperCase();
    codes.add(formatTwoFactorBackupCode(raw));
  }
  return [...codes];
}

export function parseStoredTwoFactorBackupCodeHashes(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

export type VerifyTwoFactorBackupCodeResult =
  | { valid: true; remainingHashes: string[] }
  | { valid: false; remainingHashes: string[] };

export function verifyTwoFactorBackupCodeInput(
  input: string,
  storedHashes: string[],
): VerifyTwoFactorBackupCodeResult {
  if (!isValidTwoFactorBackupCodeInput(input)) {
    return { valid: false, remainingHashes: storedHashes };
  }

  const candidate = hashTwoFactorBackupCode(input);
  const index = storedHashes.indexOf(candidate);
  if (index === -1) {
    return { valid: false, remainingHashes: storedHashes };
  }

  const remainingHashes = storedHashes.filter((_, i) => i !== index);
  return { valid: true, remainingHashes };
}

export function hashTwoFactorBackupCodes(codes: string[]): string[] {
  return codes.map((code) => hashTwoFactorBackupCode(code));
}
